/**
 * 投稿詳細 動的スコアカード列の配線 (docs/105 A-③、old2 `postDetailScores` + scoreEvaluator 移植)。
 *
 * scoreEvaluator (features/analysis) の `evaluateAllColumns` は「列 + post + レジストリ」を
 * 受け取って 2 段階解決 (aggregate → formula) で値を算出する純粋関数。old2 では
 * `buildScoreFieldRegistry(mode, postFields, outputFormatMaster)` がレジストリを事前構築し、
 * 列の `fieldPath` をその `path` と突き合わせていた。
 *
 * v2 PostDetailModal は district.mode / postFields / outputFormatMaster を保持しない
 * (M5 `analysisDisplayConfig.displayRules.postDetailScores` だけで駆動する) ため、
 * 列の `fieldPath` 自体に抽出規約を埋め込み、path 文字列を解析して descriptor を導出する
 * self-contained 方式にする。これにより modal 側は追加の master fetch なしでスコア列を描画できる。
 *
 * 対応 path 規約 (old2 `scoreFields.ts` のレジストリ path と互換):
 *   - `extra.<key>`                       → extra-field (scalar)        post.extraFields[<key>]
 *   - `ai.<mode>.classifications[].score` → ai-classifications-score (array)
 *   - `ai.walker.<overallKey>`            → ai-walker-overall (scalar)  walkable_score
 *   - `ai.<mode>.<field>.<sub>`           → ai-aggregate (scalar)       例 `ai.bousai.hotto_score.avg`
 *
 * formula 列 (fieldPath 空 or 解析不能) は descriptor 不要 (`[label]` 参照で aggregate 列の値を解決)。
 *
 * 境界遵守: scoreEvaluator は features/analysis なので **index 経由** (`../analysis`) でのみ import する。
 */
import type { AnalysisDisplayRules } from '@citizen-walk/shared-types';
import { evaluateAllColumns, type ScoreFieldDescriptor } from '../analysis';
import type { PostMinimal } from './usePosts';

/**
 * shared-types は `ScoreColumnConfig` を package root から直接 export していないため、
 * 唯一の真実 `AnalysisDisplayRules` から indexed-access で導出する。
 */
type ScoreColumnConfig = NonNullable<AnalysisDisplayRules['postDetailScores']>['columns'][number];

const AGG_SUBS = new Set(['sum', 'max', 'avg', 'min']);

/**
 * 単一の `fieldPath` を解析して `ScoreFieldDescriptor` を導出する (解析不能なら null)。
 * derived descriptor の `path` は入力 fieldPath をそのまま採用するため、
 * `evaluateAllColumns` の findDescriptor (path 一致) と確実に一致する。
 */
export function buildDescriptorFromFieldPath(fieldPath: string): ScoreFieldDescriptor | null {
  const path = fieldPath.trim();
  if (!path) return null;

  // extra.<key>
  if (path.startsWith('extra.')) {
    const key = path.slice('extra.'.length);
    if (!key) return null;
    return { path, cardinality: 'scalar', extractor: { kind: 'extra-field', key } };
  }

  if (path.startsWith('ai.')) {
    const rest = path.slice('ai.'.length);
    const segments = rest.split('.');
    const modeKey = segments[0];
    if (!modeKey) return null;

    // ai.<mode>.classifications[].score → array
    if (rest.endsWith('classifications[].score')) {
      return {
        path,
        cardinality: 'array',
        extractor: { kind: 'ai-classifications-score', modeKey },
      };
    }

    // ai.<mode>.<field>.<sub> (sub ∈ sum/max/avg/min) → aggregate scalar
    if (segments.length >= 3) {
      const sub = segments[segments.length - 1];
      const field = segments.slice(1, segments.length - 1).join('.');
      if (field && sub && AGG_SUBS.has(sub)) {
        return {
          path,
          cardinality: 'scalar',
          extractor: { kind: 'ai-aggregate', modeKey, field, sub },
        };
      }
    }

    // 【廃止予定 fallback】ai.walker.<overallKey> (集約 sub でない単一 key) → walkable_score。
    // legacy 地区 (legacyModeResults) の投稿のみ。新規は下の ai.<mode>.<key> scalar 形式を使う。
    if (modeKey === 'walker' && segments.length === 2 && segments[1]) {
      return { path, cardinality: 'scalar', extractor: { kind: 'ai-walker-overall' } };
    }

    // MD-4 モードドリブン化 (2026-07-24): ai.<mode>.<key> (集約 sub でない単一 key) →
    // mode result 直下の scalar 数値。例: `ai.generic.walkable_score` (M3 aggregateScores.overallKey)。
    if (segments.length === 2 && segments[1]) {
      return {
        path,
        cardinality: 'scalar',
        extractor: { kind: 'ai-scalar', modeKey, field: segments[1] },
      };
    }
  }

  return null;
}

/**
 * M5 `postDetailScores.columns` を評価して列順の値配列を返す。
 *
 * 各 aggregate 列の `fieldPath` から descriptor を導出してレジストリを組み、
 * scoreEvaluator の `evaluateAllColumns` (2 段階解決) に委譲する。
 * formula 列は descriptor 不要 (aggregate 列の `[label]` を参照)。
 */
export function evaluatePostDetailScores(
  columns: ScoreColumnConfig[],
  post: PostMinimal,
): (number | null)[] {
  const registry: ScoreFieldDescriptor[] = [];
  const seen = new Set<string>();
  for (const col of columns) {
    if (col.calculation.kind !== 'aggregate') continue;
    if (!col.fieldPath || seen.has(col.fieldPath)) continue;
    const desc = buildDescriptorFromFieldPath(col.fieldPath);
    if (desc) {
      registry.push(desc);
      seen.add(col.fieldPath);
    }
  }
  return evaluateAllColumns(columns, post, registry);
}
